import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, RotateCcw, FileSpreadsheet, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Right-side drawer listing the last 50 outbound imports so admins can
 * surgically revert a bad drop. Mirrors the mailer's ImportHistoryDrawer
 * behavior — Revert deletes rows that are still `outbound=true` and
 * leaves promoted / opted-out leads alone.
 */
export default function OutboundImportHistoryDrawer({ open, onClose, onReverted }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${BACKEND_URL}/api/outbound/imports`, { params: { limit: 50 } });
        if (!cancelled) setImports(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load import history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const revert = async (imp) => {
    if (!window.confirm(`Revert "${imp.campaign}" — delete rows that are still in the outbound queue?\n\nPromoted / opted-out leads will NOT be touched.`)) return;
    setRevertingId(imp.id);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/outbound/imports/${imp.id}/revert`);
      toast.success(`Reverted ${r.data.deleted} lead${r.data.deleted === 1 ? '' : 's'}`);
      // Optimistically mark the import row as reverted so the button flips.
      setImports((prev) => prev.map((x) => x.id === imp.id ? { ...x, reverted_at: new Date().toISOString() } : x));
      if (onReverted) onReverted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revert failed');
    } finally {
      setRevertingId(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[75] bg-neutral-950/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        data-testid="outbound-import-history-drawer"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white h-full shadow-2xl flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b hairline">
          <div>
            <div className="text-[13.5px] font-bold text-neutral-900">Outbound import history</div>
            <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">Last 50 drops</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-[12.5px] text-neutral-500">Loading…</div>
          ) : imports.length === 0 ? (
            <div className="p-10 text-center">
              <FileSpreadsheet className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
              <div className="text-[13px] font-semibold text-neutral-700">No imports yet</div>
              <div className="text-[11.5px] text-neutral-500 mt-1">Upload your first outbound list to see it here.</div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {imports.map((imp) => {
                const isReverted = !!imp.reverted_at;
                return (
                  <div
                    key={imp.id}
                    data-testid={`outbound-history-row-${imp.id}`}
                    className={cn('px-5 py-3.5', isReverted && 'bg-neutral-50')}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold text-neutral-900 truncate">{imp.campaign}</div>
                        <div className="text-[10.5px] text-neutral-500 mono truncate">
                          {imp.filename || imp.id} · {new Date(imp.created_at).toLocaleString()}
                        </div>
                        <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                          <Pill label="Rows" value={imp.row_count} />
                          <Pill label="Inserted" value={imp.inserted} tone="ok" />
                          {imp.skipped > 0 && <Pill label="Skipped" value={imp.skipped} tone="warn" />}
                          <Pill label="LOs" value={imp.assigned_ids?.length || 0} />
                        </div>
                        {imp.created_by_name && (
                          <div className="mt-1.5 text-[10.5px] text-neutral-500">by {imp.created_by_name}</div>
                        )}
                        {isReverted && (
                          <div className="mt-1.5 text-[10.5px] font-semibold text-red-600">
                            Reverted {imp.reverted_by_name ? `by ${imp.reverted_by_name}` : ''}
                          </div>
                        )}
                      </div>
                      {!isReverted && (
                        <button
                          data-testid={`outbound-revert-${imp.id}`}
                          onClick={() => revert(imp)}
                          disabled={revertingId === imp.id}
                          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 px-2.5 py-1 text-[11px] font-bold disabled:opacity-50"
                          title="Delete rows that are still in the outbound queue"
                        >
                          <RotateCcw className="h-3 w-3" />
                          {revertingId === imp.id ? 'Reverting…' : 'Revert'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Pill({ label, value, tone = 'default' }) {
  const cls = {
    default: 'bg-neutral-100 text-neutral-700',
    ok: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
  }[tone];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums', cls)}>
      {label} <b className="tabular-nums">{Number(value || 0).toLocaleString()}</b>
    </span>
  );
}
